import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/points-report")({
  head: () => ({ meta: [{ title: "J Raporu — Admin" }] }),
  component: PointsReportPage,
});

type TxType = "spend" | "earn" | "purchase" | "admin_adjust";

interface TxRow {
  id: string;
  user_id: string;
  amount: number;
  type: TxType;
  description: string | null;
  created_at: string;
}

interface ProfileLite {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  points_balance: number;
}

interface UserAgg {
  user_id: string;
  loadedPurchase: number; // Shopier / paket
  loadedAdmin: number; // Admin manuel +
  earned: number; // İçecek cashback
  spent: number; // Harcama (pozitif gösterim)
  adminDeducted: number; // Admin manuel -
  txCount: number;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function PointsReportPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [from, setFrom] = useState<string>(daysAgoIso(30));
  const [to, setTo] = useState<string>(todayIso());
  const [txs, setTxs] = useState<TxRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [holders, setHolders] = useState<ProfileLite[]>([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [holderSearch, setHolderSearch] = useState("");

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) void navigate({ to: "/" });
  }, [loading, user, isAdmin, navigate]);

  const load = async () => {
    setBusy(true);
    const fromTs = new Date(from + "T00:00:00").toISOString();
    const toTs = new Date(to + "T23:59:59").toISOString();
    const { data, error } = await supabase
      .from("point_transactions")
      .select("id, user_id, amount, type, description, created_at")
      .gte("created_at", fromTs)
      .lte("created_at", toTs)
      .order("created_at", { ascending: false })
      .limit(10000);
    if (error) {
      setBusy(false);
      return;
    }
    const list = (data as TxRow[]) ?? [];
    setTxs(list);
    const ids = Array.from(new Set(list.map((t) => t.user_id)));
    if (ids.length > 0) {
      const { data: pr } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email, points_balance")
        .in("id", ids);
      const map: Record<string, ProfileLite> = {};
      for (const p of (pr as ProfileLite[]) ?? []) map[p.id] = p;
      setProfiles(map);
    } else {
      setProfiles({});
    }
    // Load ALL members with positive J balance (paginated to bypass 1000 row limit)
    const EXCLUDED = new Set(["5c4a883a-d8a5-4f1f-aa6e-8b2c731d4d3e"]);
    const all: ProfileLite[] = [];
    const PAGE = 1000;
    for (let offset = 0; ; offset += PAGE) {
      const { data: hr, error: he } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email, points_balance")
        .gt("points_balance", 0)
        .order("points_balance", { ascending: false })
        .range(offset, offset + PAGE - 1);
      if (he || !hr || hr.length === 0) break;
      all.push(...((hr as ProfileLite[]) ?? []));
      if (hr.length < PAGE) break;
    }
    setHolders(all.filter((p) => !EXCLUDED.has(p.id)));
    setBusy(false);
  };



  useEffect(() => {
    if (user && isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const totals = useMemo(() => {
    let loadedPurchase = 0;
    let loadedAdmin = 0;
    let earned = 0;
    let spent = 0;
    let adminDeducted = 0;
    for (const t of txs) {
      if (t.type === "purchase") loadedPurchase += t.amount;
      else if (t.type === "admin_adjust") {
        if (t.amount >= 0) loadedAdmin += t.amount;
        else adminDeducted += -t.amount;
      } else if (t.type === "earn") earned += t.amount;
      else if (t.type === "spend") spent += -t.amount; // amount negatif
    }
    return { loadedPurchase, loadedAdmin, earned, spent, adminDeducted };
  }, [txs]);

  const perUser = useMemo(() => {
    const map = new Map<string, UserAgg>();
    for (const t of txs) {
      const a = map.get(t.user_id) ?? {
        user_id: t.user_id,
        loadedPurchase: 0,
        loadedAdmin: 0,
        earned: 0,
        spent: 0,
        adminDeducted: 0,
        txCount: 0,
      };
      a.txCount += 1;
      if (t.type === "purchase") a.loadedPurchase += t.amount;
      else if (t.type === "admin_adjust") {
        if (t.amount >= 0) a.loadedAdmin += t.amount;
        else a.adminDeducted += -t.amount;
      } else if (t.type === "earn") a.earned += t.amount;
      else if (t.type === "spend") a.spent += -t.amount;
      map.set(t.user_id, a);
    }
    const arr = Array.from(map.values());
    arr.sort(
      (x, y) =>
        y.loadedPurchase + y.loadedAdmin + y.spent - (x.loadedPurchase + x.loadedAdmin + x.spent),
    );
    return arr;
  }, [txs]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return perUser;
    return perUser.filter((u) => {
      const p = profiles[u.user_id];
      if (!p) return false;
      return (
        p.first_name.toLowerCase().includes(q) ||
        p.last_name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q)
      );
    });
  }, [perUser, profiles, search]);

  const fmt = (p?: ProfileLite) =>
    p ? `${p.first_name} ${p.last_name}`.trim() || p.email : "—";

  const exportCsv = () => {
    const headers = [
      "Ad Soyad",
      "E-posta",
      "Yüklenen (Shopier/Paket)",
      "Yüklenen (Admin)",
      "Kazanılan (Cashback)",
      "Harcanan",
      "Admin Düşülen",
      "Mevcut Bakiye",
      "İşlem Sayısı",
    ];
    const lines = [headers.join(",")];
    for (const u of filteredUsers) {
      const p = profiles[u.user_id];
      const cells = [
        `"${(p ? `${p.first_name} ${p.last_name}`.trim() : "").replace(/"/g, '""')}"`,
        `"${p?.email ?? ""}"`,
        u.loadedPurchase,
        u.loadedAdmin,
        u.earned,
        u.spent,
        u.adminDeducted,
        p?.points_balance ?? 0,
        u.txCount,
      ];
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `j-raporu-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !user || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-6xl px-4 py-10 text-center text-muted-foreground">
          Yükleniyor...
        </div>
      </div>
    );
  }

  const totalLoaded = totals.loadedPurchase + totals.loadedAdmin;
  const netCirculation = totalLoaded + totals.earned - totals.spent - totals.adminDeducted;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">Admin</h1>
        <AdminTabs />

        <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="from">Başlangıç</Label>
              <Input
                id="from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="to">Bitiş</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <Button onClick={() => void load()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uygula"}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={txs.length === 0}>
              <Download className="mr-1.5 h-4 w-4" /> CSV
            </Button>
            <div className="ml-auto flex flex-wrap gap-2 text-xs">
              <button
                className="rounded-full border border-border px-3 py-1 hover:bg-muted"
                onClick={() => {
                  setFrom(daysAgoIso(7));
                  setTo(todayIso());
                }}
              >
                Son 7 gün
              </button>
              <button
                className="rounded-full border border-border px-3 py-1 hover:bg-muted"
                onClick={() => {
                  setFrom(daysAgoIso(30));
                  setTo(todayIso());
                }}
              >
                Son 30 gün
              </button>
              <button
                className="rounded-full border border-border px-3 py-1 hover:bg-muted"
                onClick={() => {
                  setFrom(daysAgoIso(90));
                  setTo(todayIso());
                }}
              >
                Son 90 gün
              </button>
            </div>
          </div>
        </section>

        <section className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Toplam yüklenen" value={totalLoaded} hint="Shopier + Admin" tone="success" />
          <StatCard label="Shopier / Paket" value={totals.loadedPurchase} tone="default" />
          <StatCard label="Admin yüklemesi" value={totals.loadedAdmin} tone="default" />
          <StatCard label="Cashback (kazanım)" value={totals.earned} tone="default" />
          <StatCard label="Toplam harcanan" value={totals.spent} tone="warn" />
          <StatCard label="Admin düşümü" value={totals.adminDeducted} tone="warn" />
          <StatCard
            label="Net bakiye değişimi"
            value={netCirculation}
            tone={netCirculation >= 0 ? "success" : "warn"}
            hint="Yüklenen + cashback − harcanan − düşülen"
          />
          <StatCard label="İşlem sayısı" value={txs.length} tone="default" suffix="" />
        </section>

        <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Üye bazlı kırılım</h2>
              <p className="text-sm text-muted-foreground">
                Seçili tarih aralığında üyelerin yükleme ve harcamaları. Mevcut bakiye anlık olarak
                gösterilir.
              </p>
            </div>
            <Input
              placeholder="Üye ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Üye</th>
                  <th className="py-2 px-3 text-right">Yüklenen</th>
                  <th className="py-2 px-3 text-right">Cashback</th>
                  <th className="py-2 px-3 text-right">Harcanan</th>
                  <th className="py-2 px-3 text-right">Admin düşümü</th>
                  <th className="py-2 pl-3 text-right">Bakiye</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const p = profiles[u.user_id];
                  return (
                    <tr key={u.user_id} className="border-b border-border/50">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{fmt(p)}</div>
                        <div className="text-xs text-muted-foreground">{p?.email}</div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="font-medium text-success">
                          {(u.loadedPurchase + u.loadedAdmin).toLocaleString("tr-TR")} J
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {u.loadedPurchase} shopier · {u.loadedAdmin} admin
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">{u.earned.toLocaleString("tr-TR")} J</td>
                      <td className="py-2 px-3 text-right text-destructive">
                        {u.spent.toLocaleString("tr-TR")} J
                      </td>
                      <td className="py-2 px-3 text-right text-muted-foreground">
                        {u.adminDeducted ? `${u.adminDeducted.toLocaleString("tr-TR")} J` : "—"}
                      </td>
                      <td className="py-2 pl-3 text-right font-semibold">
                        {(p?.points_balance ?? 0).toLocaleString("tr-TR")} J
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-muted-foreground">
                      Bu aralıkta işlem bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Bakiyesi olan üyeler</h2>
              <p className="text-sm text-muted-foreground">
                Şu an cüzdanında J bulunan tüm üyeler. Tarih filtresinden bağımsız, anlık bakiye.
              </p>
            </div>
            <Input
              placeholder="Üye ara"
              value={holderSearch}
              onChange={(e) => setHolderSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>

          {(() => {
            const q = holderSearch.trim().toLowerCase();
            const filtered = q
              ? holders.filter(
                  (p) =>
                    p.first_name.toLowerCase().includes(q) ||
                    p.last_name.toLowerCase().includes(q) ||
                    p.email.toLowerCase().includes(q),
                )
              : holders;
            const totalBalance = holders.reduce((s, p) => s + (p.points_balance ?? 0), 0);
            const filteredBalance = filtered.reduce((s, p) => s + (p.points_balance ?? 0), 0);
            return (
              <>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <StatCard label="Bakiyeli üye" value={holders.length} tone="default" suffix="" />
                  <StatCard label="Toplam bakiye" value={totalBalance} tone="success" />
                  <StatCard
                    label={q ? "Filtreli toplam" : "Ortalama bakiye"}
                    value={q ? filteredBalance : Math.round(totalBalance / Math.max(holders.length, 1))}
                    tone="default"
                  />
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full min-w-[600px] text-sm">
                    <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="py-2 pr-3 w-10">#</th>
                        <th className="py-2 px-3">Üye</th>
                        <th className="py-2 pl-3 text-right">Bakiye</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p, i) => (
                        <tr key={p.id} className="border-b border-border/50">
                          <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                          <td className="py-2 px-3">
                            <div className="font-medium">
                              {`${p.first_name} ${p.last_name}`.trim() || p.email}
                            </div>
                            <div className="text-xs text-muted-foreground">{p.email}</div>
                          </td>
                          <td className="py-2 pl-3 text-right font-semibold text-success">
                            {(p.points_balance ?? 0).toLocaleString("tr-TR")} J
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={3} className="py-6 text-center text-muted-foreground">
                            Bakiyesi olan üye yok.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </section>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone = "default",
  suffix = " J",
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: "default" | "success" | "warn";
  suffix?: string;
}) {
  const color =
    tone === "success"
      ? "text-success"
      : tone === "warn"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${color}`}>
        {value.toLocaleString("tr-TR")}
        {suffix}
      </div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
